export type DecisionOption = {
  label: string;
  nextNodeId?: string;
  resultId?: string;
};

export type DecisionNode = {
  id: string;
  question: string;
  hint: string;
  options: DecisionOption[];
};

export type DecisionResultLink = {
  label: string;
  url: string;
};

export type DecisionResult = {
  title: string;
  description: string;
  details: string[];
  links: DecisionResultLink[];
  cta?: DecisionResultLink;
};

export type DecisionPathEntry = {
  nodeId: string;
  choiceLabel: string;
};

const BACKEND_API_HINT =
  'Choose Yes if your plugin needs its own endpoints in the Backstage backend. Choose No if it only needs frontend code.';

const NODES: Record<string, DecisionNode> = {
  start: {
    id: 'start',
    question: 'Will this tool automate the setup of a new project?',
    hint: 'For example, automating the steps to create a repo, service skeleton, or onboarding flow.',
    options: [
      { label: 'Yes', resultId: 'template' },
      { label: 'No', nextNodeId: 'catalog' },
    ],
  },
  catalog: {
    id: 'catalog',
    question:
      'Will your tool automatically discover or annotate entities in the Software Catalog?',
    hint: 'For example, auto-filling missing fields on entities or syncing data from an external system into the catalog.',
    options: [
      { label: 'Yes', resultId: 'backend-module' },
      { label: 'No', nextNodeId: 'ui' },
    ],
  },
  ui: {
    id: 'ui',
    question: 'Does your project need its own UI in Backstage?',
    hint: 'Choose the type of surface your feature needs inside the portal.',
    options: [
      { label: 'No, backend only', resultId: 'backend-plugin' },
      { label: 'Yes, a full page', nextNodeId: 'api-page' },
      { label: 'Yes, an entity tab', nextNodeId: 'api-tab' },
      { label: 'Yes, a home widget', nextNodeId: 'api-widget' },
    ],
  },
  'api-page': {
    id: 'api-page',
    question:
      'Will the full page need to fetch data from a custom backend API?',
    hint: BACKEND_API_HINT,
    options: [
      { label: 'Yes', resultId: 'plugin-pair-page' },
      { label: 'No', resultId: 'page-blueprint' },
    ],
  },
  'api-tab': {
    id: 'api-tab',
    question:
      'Will the entity tab need to fetch data from a custom backend API?',
    hint: BACKEND_API_HINT,
    options: [
      { label: 'Yes', resultId: 'plugin-pair-tab' },
      { label: 'No', resultId: 'entity-content-blueprint' },
    ],
  },
  'api-widget': {
    id: 'api-widget',
    question:
      'Will the home widget need to fetch data from a custom backend API?',
    hint: BACKEND_API_HINT,
    options: [
      { label: 'Yes', resultId: 'plugin-pair-widget' },
      { label: 'No', resultId: 'home-widget-blueprint' },
    ],
  },
};

const RESULTS: Record<string, DecisionResult> = {
  template: {
    title: 'Scaffolder Template',
    description:
      'A template automates the one-time setup of a software project — like creating a new repo, service, or onboarding flow. It runs as a temporary task in the Scaffolder and is defined in YAML using Nunjucks templating.',
    details: [
      'Defined in YAML with kind: Template',
      'Runs once per execution in the Scaffolder task runner',
      'Uses built-in actions like fetch:template, publish:github, catalog:register',
      'No PR needed when using existing actions — deploy by updating your YAML',
    ],
    links: [
      {
        label: 'Start creating your own template',
        url: '/create/edit',
      },
      {
        label: 'Software Templates docs',
        url: 'https://backstage.io/docs/features/software-templates/',
      },
      {
        label: 'Built-in scaffolder actions',
        url: 'https://backstage.io/docs/features/software-templates/builtin-actions',
      },
    ],
    cta: {
      label: 'Read the template guide',
      url: '/docs/default/component/backstage/contribute/create-a-template/',
    },
  },
  'backend-module': {
    title: 'Backend Module — Catalog Processor',
    description:
      'A backend module that plugs into the Backstage catalog via CatalogProcessor. Use createBackendModule() to annotate entities (e.g. auto-filling missing metadata) or discover new entities from external systems.',
    details: [
      'Created with createBackendModule()',
      'Extends catalogProcessingExtensionPoint',
      'Runs inside the Backstage backend process',
      'Registered via backend.add(import(…)) in packages/backend',
    ],
    links: [
      {
        label: 'Creating a new Backend Module',
        url: 'https://backstage.io/docs/backend-system/building-plugins-and-modules/index',
      },
      {
        label: 'Backend system docs',
        url: 'https://backstage.io/docs/backend-system/',
      },
    ],
    cta: {
      label: 'Read the plugin guide',
      url: '/docs/default/component/backstage/contribute/create-a-plugin/',
    },
  },
  'backend-plugin': {
    title: 'Backend Plugin',
    description:
      'A standalone backend plugin that exposes REST API endpoints. Use createBackendPlugin() to register an Express router via httpRouter.',
    details: [
      'Created with createBackendPlugin()',
      'Exposes REST API endpoints via httpRouter',
      'Long-lived — always available while the backend runs',
      'Registered via backend.add(import(…)) in packages/backend',
    ],
    links: [
      {
        label: 'Backend system docs',
        url: 'https://backstage.io/docs/backend-system/',
      },
    ],
    cta: {
      label: 'Read the plugin guide',
      url: '/docs/default/component/backstage/contribute/create-a-plugin/',
    },
  },
  'page-blueprint': {
    title: 'Frontend Plugin — PageBlueprint',
    description:
      'A frontend plugin that adds a new full page at a dedicated route (e.g. /my-tool). Use createFrontendPlugin() with PageBlueprint.make() to register the page.',
    details: [
      'Created with createFrontendPlugin() in plugin.tsx',
      'Page defined via PageBlueprint.make()',
      'title + icon on PageBlueprint auto-registers sidebar nav',
      'Wired into packages/app/src/app.tsx under features[]',
    ],
    links: [
      {
        label: 'New Frontend System',
        url: 'https://backstage.io/docs/frontend-system/',
      },
    ],
    cta: {
      label: 'Read the plugin guide',
      url: '/docs/default/component/backstage/contribute/create-a-plugin/',
    },
  },
  'entity-content-blueprint': {
    title: 'Frontend Plugin — EntityContentBlueprint',
    description:
      'A frontend plugin that adds a tab on catalog entity pages. Use EntityContentBlueprint to attach content to specific entity kinds.',
    details: [
      'Created with createFrontendPlugin() in plugin.tsx',
      'Content defined via EntityContentBlueprint',
      'Appears as a tab on entity pages in the catalog',
      'Wired into packages/app/src/app.tsx under features[]',
    ],
    links: [
      {
        label: 'See an EntityContent example',
        url: 'https://backstage.io/docs/frontend-system/building-plugins/common-extension-blueprints/#entitycontent---example',
      },
      {
        label: 'New Frontend System',
        url: 'https://backstage.io/docs/frontend-system/',
      },
    ],
    cta: {
      label: 'Read the plugin guide',
      url: '/docs/default/component/backstage/contribute/create-a-plugin/',
    },
  },
  'home-widget-blueprint': {
    title: 'Frontend Plugin — HomePageWidgetBlueprint',
    description:
      'A frontend plugin that adds a widget to the Backstage home page. Use HomePageWidgetBlueprint to define a self-contained card or panel.',
    details: [
      'Created with createFrontendPlugin() in plugin.tsx',
      'Widget defined via HomePageWidgetBlueprint',
      'Appears on the Backstage home page',
      'Wired into packages/app/src/app.tsx under features[]',
    ],
    links: [
      {
        label: 'New Frontend System',
        url: 'https://backstage.io/docs/frontend-system/',
      },
    ],
    cta: {
      label: 'Read the plugin guide',
      url: '/docs/default/component/backstage/contribute/create-a-plugin/',
    },
  },
  'plugin-pair-page': {
    title: 'Frontend + Backend Plugin Pair — Full Page',
    description:
      'When you need both a UI and your own backend API, build a frontend plugin paired with a backend plugin. The frontend calls the backend via a Backstage API factory.',
    details: [
      'Frontend: createFrontendPlugin() with PageBlueprint.make()',
      'Backend: createBackendPlugin() exposing REST API via httpRouter',
      'Connected via ApiBlueprint using createApiFactory()',
      'Both registered separately in app.tsx and backend/src/index.ts',
    ],
    links: [
      {
        label: 'New Frontend System',
        url: 'https://backstage.io/docs/frontend-system/',
      },
      {
        label: 'Backend system docs',
        url: 'https://backstage.io/docs/backend-system/',
      },
    ],
    cta: {
      label: 'Read the plugin guide',
      url: '/docs/default/component/backstage/contribute/create-a-plugin/',
    },
  },
  'plugin-pair-tab': {
    title: 'Frontend + Backend Plugin Pair — Entity Tab',
    description:
      'An entity-tab frontend plugin paired with a backend plugin. The tab fetches data from your own Backstage backend via a Backstage API factory.',
    details: [
      'Frontend: createFrontendPlugin() with EntityContentBlueprint',
      'Backend: createBackendPlugin() exposing REST API via httpRouter',
      'Connected via ApiBlueprint using createApiFactory()',
      'Both registered separately in app.tsx and backend/src/index.ts',
    ],
    links: [
      {
        label: 'New Frontend System',
        url: 'https://backstage.io/docs/frontend-system/',
      },
      {
        label: 'Backend system docs',
        url: 'https://backstage.io/docs/backend-system/',
      },
    ],
    cta: {
      label: 'Read the plugin guide',
      url: '/docs/default/component/backstage/contribute/create-a-plugin/',
    },
  },
  'plugin-pair-widget': {
    title: 'Frontend + Backend Plugin Pair — Home Widget',
    description:
      'A home-page widget frontend plugin paired with a backend plugin. The widget fetches data from your own Backstage backend via a Backstage API factory.',
    details: [
      'Frontend: createFrontendPlugin() with HomePageWidgetBlueprint',
      'Backend: createBackendPlugin() exposing REST API via httpRouter',
      'Connected via ApiBlueprint using createApiFactory()',
      'Both registered separately in app.tsx and backend/src/index.ts',
    ],
    links: [
      {
        label: 'New Frontend System',
        url: 'https://backstage.io/docs/frontend-system/',
      },
      {
        label: 'Backend system docs',
        url: 'https://backstage.io/docs/backend-system/',
      },
    ],
    cta: {
      label: 'Read the plugin guide',
      url: '/docs/default/component/backstage/contribute/create-a-plugin/',
    },
  },
};

export const NODE_ORDER = [
  'start',
  'catalog',
  'ui',
  'api-page',
  'api-tab',
  'api-widget',
] as const;
export const DECISION_NODES = NODES;
export const DECISION_RESULTS = RESULTS;
export const START_NODE_ID = 'start';
